import React from 'react';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/apiConfig';

export interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  organizationId?: string;
  organizationName?: string;
  organizationRole?: 'owner' | 'admin' | 'member';
  token?: string;
  preferences?: Record<string, any>;
}

// Simple hook that wraps the auth store for backwards compatibility
export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    login: storeLogin,
    logout: storeLogout,
    updateUser: storeUpdateUser,
  } = useAuthStore();

  const login = async (email: string, password: string): Promise<void> => {
    try {
      console.log('[useAuth] Logging in:', email);
      const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
      const response = await axios.post(`${authServiceUrl}/api/v1/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token, user: authUser } = response.data.data;
      
      // Transform the auth service user to match our User interface
      const transformedUser: User = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.full_name || authUser.name,
        firstName: authUser.full_name?.split(' ')[0] || '',
        lastName: authUser.full_name?.split(' ')[1] || '',
        organizationId: authUser.organizationId || authUser.organization_id,
        organizationName: authUser.organizationName || authUser.organization_name,
        organizationRole: authUser.organizationRole || authUser.organization_role,
        role: authUser.role || 'user',
      };
      
      console.log('[useAuth] Login successful, user:', transformedUser);
      
      // Store refresh token in localStorage
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      
      // Use the login method that accepts refresh token
      storeLogin(transformedUser, access_token, refresh_token);
      navigate('/chat');
    } catch (error: any) {
      console.error('[useAuth] Login error:', error);
      throw new Error(error.response?.data?.message || 'Invalid email or password');
    }
  };

  const logout = () => {
    console.log('[useAuth] Logging out');
    storeLogout();
    navigate('/login');
  };

  const refreshToken = async (): Promise<void> => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      console.warn('[useAuth] No refresh token available');
      return;
    }
    
    try {
      console.log('[useAuth] Refreshing token');
      const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
      const response = await axios.post(
        `${authServiceUrl}/api/v1/auth/refresh`,
        {
          refresh_token: storedRefreshToken
        }
      );

      const { access_token, refresh_token: newRefreshToken } = response.data.data;
      
      // Update tokens in localStorage
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('token', access_token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      // Update token in store
      useAuthStore.getState().setToken(access_token);
      console.log('[useAuth] Token refreshed successfully');
    } catch (error) {
      console.error('[useAuth] Token refresh error:', error);
      logout();
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading: false,
    login,
    logout,
    refreshToken,
    updateUser: storeUpdateUser,
  };
};

// Export a dummy AuthProvider for backwards compatibility
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};